import { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { MagnifyingGlass, ChatCircle, Star, Plus, FloppyDisk, TrashSimple, Clock, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

// Section J + Round 36 #2
//   • Top half — admin authors custom feedback questions per course
//     (POST → /api/courses/{id}/feedback-questions). Rendered above the
//     hardcoded form on the student-facing CourseFeedbackForm.
//   • Bottom half — read-only view of every submission with CSV export.
export default function AdminFeedbackPage() {
  const [rows, setRows] = useState([]);
  const [courses, setCourses] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [courseId, setCourseId] = useState("all");
  const [programmeFilter, setProgrammeFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  // ── Manage questions state ───────────────────────────────────
  const [mgmtCourseId, setMgmtCourseId] = useState("");
  const [questions, setQuestions] = useState([]);
  const [mgmtLoading, setMgmtLoading] = useState(false);
  const [mgmtSaving, setMgmtSaving] = useState(false);

  // Round 38 — feedback release schedule (global + per-course override).
  const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const [globalSchedule, setGlobalSchedule] = useState({ day: 5, hour: 13, minute: 0, enabled: true });
  const [courseSchedule, setCourseSchedule] = useState({ day: 5, hour: 13, minute: 0, enabled: true, source: "global" });
  const [scheduleSaving, setScheduleSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/feedback-schedule")
      .then(r => setGlobalSchedule({
        day: r.data?.day ?? 5,
        hour: r.data?.hour ?? 13,
        minute: r.data?.minute ?? 0,
        enabled: r.data?.enabled !== false,
      }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!mgmtCourseId) return;
    api.get(`/courses/${mgmtCourseId}/feedback/schedule`)
      .then(r => setCourseSchedule({
        day: r.data?.day ?? 5,
        hour: r.data?.hour ?? 13,
        minute: r.data?.minute ?? 0,
        enabled: r.data?.enabled !== false,
        source: r.data?.source || "global",
      }))
      .catch(() => {});
  }, [mgmtCourseId]);

  const saveGlobalSchedule = async () => {
    setScheduleSaving(true);
    try {
      await api.put("/admin/feedback-schedule", globalSchedule);
      toast.success(`Global schedule set to ${WEEKDAYS[globalSchedule.day]} ${String(globalSchedule.hour).padStart(2,"0")}:${String(globalSchedule.minute).padStart(2,"0")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save schedule");
    }
    setScheduleSaving(false);
  };

  const saveCourseSchedule = async () => {
    if (!mgmtCourseId) return;
    setScheduleSaving(true);
    try {
      await api.put(`/courses/${mgmtCourseId}/feedback/schedule`, {
        day: courseSchedule.day, hour: courseSchedule.hour,
        minute: courseSchedule.minute, enabled: courseSchedule.enabled,
      });
      toast.success("Course override saved");
      setCourseSchedule(prev => ({ ...prev, source: "course" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save override");
    }
    setScheduleSaving(false);
  };

  const clearCourseSchedule = async () => {
    if (!mgmtCourseId) return;
    setScheduleSaving(true);
    try {
      await api.put(`/courses/${mgmtCourseId}/feedback/schedule`, { clear: true });
      toast.success("Course now follows the global schedule");
      setCourseSchedule({ ...globalSchedule, source: "global" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear override");
    }
    setScheduleSaving(false);
  };

  // Round 38 — Admission number configuration (Task 11).
  const [admFormat, setAdmFormat] = useState({ format: "SBL-{yyyy}{month_letter}-{programme}-{seq:03d}", institution_code: "SBL" });
  const [admPreview, setAdmPreview] = useState("");
  const [admBackfillResult, setAdmBackfillResult] = useState(null);
  useEffect(() => {
    api.get("/admin/admission-number-format").then(r => {
      setAdmFormat({ format: r.data?.format || "", institution_code: r.data?.institution_code || "SBL" });
      // Fetch preview.
      api.get("/admin/admission-numbers/preview", { params: { programme_code: "P001", seq: 19 } })
        .then(rr => setAdmPreview(rr.data?.sample || ""))
        .catch(() => {});
    }).catch(() => {});
  }, []);
  const saveAdmFormat = async () => {
    try {
      const r = await api.put("/admin/admission-number-format", admFormat);
      setAdmPreview(r.data?.sample || "");
      toast.success("Admission number format saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save format");
    }
  };
  const runBackfill = async () => {
    if (!window.confirm("Assign admission numbers to every enrolment that doesn't have one? Safe to run multiple times.")) return;
    try {
      const r = await api.post("/admin/admission-numbers/backfill");
      setAdmBackfillResult(r.data);
      toast.success(`Assigned ${r.data.assigned} admission number(s) (${r.data.scanned} scanned)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    }
  };

  const loadDefaultQuestions = async () => {
    try {
      const r = await api.get("/feedback/default-questions");
      const defaults = Array.isArray(r.data) ? r.data : [];
      setQuestions(defaults.map(q => ({ ...q, id: `${q.id}-${Date.now().toString(36)}` })));
      toast.success(`Loaded ${defaults.length} default question(s) — remember to click Save.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load defaults");
    }
  };

  useEffect(() => {
    api.get("/courses").then(r => {
      const list = r.data || [];
      setCourses(list);
      if (list.length > 0 && !mgmtCourseId) setMgmtCourseId(list[0].id);
    }).catch(() => {});
    api.get("/programmes").then(r => setProgrammes(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mgmtCourseId) return;
    setMgmtLoading(true);
    api.get(`/courses/${mgmtCourseId}/feedback-questions`)
      .then(r => setQuestions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setQuestions([]))
      .finally(() => setMgmtLoading(false));
  }, [mgmtCourseId]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (courseId !== "all") params.append("course_id", courseId);
    if (programmeFilter !== "all") params.append("programme_id", programmeFilter);
    if (q) params.append("q", q);
    api.get(`/admin/feedback?${params.toString()}`)
      .then(r => { setRows(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [courseId, programmeFilter, q]);

  const avg = useMemo(() => {
    const vals = rows
      .map(r => Number(r.overall_rating || r.rating || 0))
      .filter(v => v > 0);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2) : "—";
  }, [rows]);

  const addQuestion = () => {
    setQuestions(prev => [
      ...prev,
      { id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, question_text: "", type: "rating" },
    ]);
  };

  const updateQuestion = (idx, patch) => {
    setQuestions(prev => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };

  const removeQuestion = (idx) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const saveQuestions = async () => {
    if (!mgmtCourseId) return;
    const cleaned = questions
      .map(q => ({ ...q, question_text: (q.question_text || "").trim() }))
      .filter(q => q.question_text);
    if (cleaned.length !== questions.length) {
      toast.error("Empty questions removed before save");
    }
    setMgmtSaving(true);
    try {
      const res = await api.put(`/courses/${mgmtCourseId}/feedback-questions`, cleaned);
      toast.success(`Saved ${res.data?.count ?? cleaned.length} question(s)`);
      setQuestions(cleaned);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save questions");
    }
    setMgmtSaving(false);
  };

  const exportCsv = () => {
    const header = ["created_at", "user_email", "user_name", "programme_title", "course_title", "overall_rating", "content_quality", "instructor_rating", "pace_rating", "would_recommend", "most_valuable", "improvements", "additional_comments"];
    const lines = [header.join(",")];
    for (const r of rows) {
      const safe = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      lines.push(header.map(k => safe(r[k])).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `feedback-${Date.now()}.csv`;
    a.click();
  };

  const exportXlsx = async () => {
    try {
      const params = new URLSearchParams();
      if (courseId !== "all") params.append("course_id", courseId);
      if (programmeFilter !== "all") params.append("programme_id", programmeFilter);
      if (q) params.append("q", q);
      const res = await api.get(`/admin/feedback/export?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `course-feedback-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} row(s) to Excel`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12 max-w-6xl mx-auto" data-testid="admin-feedback-page">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Admin · Feedback</p>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Course feedback &amp; evaluation submissions
          </h1>
        </div>

        {/* Round 38 — Admission number system (Task 11) */}
        <Card className="border border-emerald-200 bg-emerald-50/30 rounded-none mb-6" data-testid="admission-number-card">
          <CardContent className="p-5 space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Round 38 · Item 11</p>
              <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Admission number system
              </h2>
              <p className="text-xs text-slate-600 mt-0.5">
                Configure the auto-generated format used when students enrol into a programme. Supported tokens:{" "}
                <code className="px-1 bg-white">{"{institution}"}</code>{" "}
                <code className="px-1 bg-white">{"{yyyy}"}</code>{" "}
                <code className="px-1 bg-white">{"{month_letter}"}</code>{" "}
                <code className="px-1 bg-white">{"{programme}"}</code>{" "}
                <code className="px-1 bg-white">{"{seq:03d}"}</code>.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-32">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Institution</Label>
                <Input value={admFormat.institution_code} onChange={e => setAdmFormat(s => ({ ...s, institution_code: e.target.value.toUpperCase() }))} className="rounded-none mt-1" data-testid="adm-institution-code" />
              </div>
              <div className="flex-1 min-w-[280px]">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Format template</Label>
                <Input value={admFormat.format} onChange={e => setAdmFormat(s => ({ ...s, format: e.target.value }))} className="rounded-none mt-1 font-mono text-xs" data-testid="adm-format-template" />
              </div>
              <button onClick={saveAdmFormat} className="px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm flex items-center gap-1 rounded-none" data-testid="adm-format-save">
                <FloppyDisk size={14} /> Save format
              </button>
              <button onClick={runBackfill} className="px-3 py-2 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 text-sm flex items-center gap-1 rounded-none" data-testid="adm-backfill">
                Backfill legacy enrolments
              </button>
            </div>
            {admPreview && (
              <div className="text-xs text-emerald-900 bg-white border border-emerald-100 p-2 font-mono" data-testid="adm-preview">
                Preview (programme P001, seq 19): <b>{admPreview}</b>
              </div>
            )}
            {admBackfillResult && (
              <p className="text-xs text-slate-700" data-testid="adm-backfill-result">
                Scanned {admBackfillResult.scanned} enrolment(s), assigned {admBackfillResult.assigned}.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Round 38 — feedback release schedule editor */}
        <Card className="border border-blue-200 bg-blue-50/40 rounded-none mb-6" data-testid="feedback-schedule-card">          <CardContent className="p-5 space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-blue-700 font-bold flex items-center gap-1.5">
                <Clock size={12} /> Round 38 · Item 3
              </p>
              <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Feedback release schedule
              </h2>
              <p className="text-xs text-slate-600 mt-0.5">
                Set the weekly opening window for course feedback in the student&apos;s local time. Course-completed students always bypass the schedule.
              </p>
            </div>

            {/* Global schedule row */}
            <div className="border border-slate-200 bg-white p-3 space-y-3">
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Global default</p>
              <div className="flex flex-wrap items-end gap-2">
                <div className="w-40">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Day</Label>
                  <Select value={String(globalSchedule.day)} onValueChange={v => setGlobalSchedule(s => ({ ...s, day: parseInt(v) }))}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="global-schedule-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map((n, i) => <SelectItem key={n} value={String(i)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-24">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Hour (0-23)</Label>
                  <Input type="number" min={0} max={23} value={globalSchedule.hour}
                    onChange={e => setGlobalSchedule(s => ({ ...s, hour: Math.max(0, Math.min(23, parseInt(e.target.value || "0"))) }))}
                    className="rounded-none mt-1" data-testid="global-schedule-hour" />
                </div>
                <div className="w-24">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Minute</Label>
                  <Input type="number" min={0} max={59} value={globalSchedule.minute}
                    onChange={e => setGlobalSchedule(s => ({ ...s, minute: Math.max(0, Math.min(59, parseInt(e.target.value || "0"))) }))}
                    className="rounded-none mt-1" data-testid="global-schedule-minute" />
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-700 pb-2 cursor-pointer">
                  <input type="checkbox" checked={globalSchedule.enabled}
                    onChange={e => setGlobalSchedule(s => ({ ...s, enabled: e.target.checked }))}
                    data-testid="global-schedule-enabled" />
                  Enforce
                </label>
                <button onClick={saveGlobalSchedule} disabled={scheduleSaving}
                  className="px-3 py-2 bg-blue-800 text-white hover:bg-blue-900 disabled:opacity-50 text-sm flex items-center gap-1 rounded-none"
                  data-testid="global-schedule-save">
                  <FloppyDisk size={14} /> {scheduleSaving ? "Saving…" : "Save default"}
                </button>
              </div>
            </div>

            {/* Per-course override row */}
            {mgmtCourseId && (
              <div className="border border-slate-200 bg-white p-3 space-y-3">
                <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-2">
                  Course override · <span className="font-normal normal-case text-slate-500">Applies only to this course. Source: <b>{courseSchedule.source}</b></span>
                </p>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="w-40">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Day</Label>
                    <Select value={String(courseSchedule.day)} onValueChange={v => setCourseSchedule(s => ({ ...s, day: parseInt(v) }))}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="course-schedule-day"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {WEEKDAYS.map((n, i) => <SelectItem key={n} value={String(i)}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Hour</Label>
                    <Input type="number" min={0} max={23} value={courseSchedule.hour}
                      onChange={e => setCourseSchedule(s => ({ ...s, hour: Math.max(0, Math.min(23, parseInt(e.target.value || "0"))) }))}
                      className="rounded-none mt-1" data-testid="course-schedule-hour" />
                  </div>
                  <div className="w-24">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Minute</Label>
                    <Input type="number" min={0} max={59} value={courseSchedule.minute}
                      onChange={e => setCourseSchedule(s => ({ ...s, minute: Math.max(0, Math.min(59, parseInt(e.target.value || "0"))) }))}
                      className="rounded-none mt-1" data-testid="course-schedule-minute" />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-700 pb-2 cursor-pointer">
                    <input type="checkbox" checked={courseSchedule.enabled}
                      onChange={e => setCourseSchedule(s => ({ ...s, enabled: e.target.checked }))}
                      data-testid="course-schedule-enabled" />
                    Enforce
                  </label>
                  <button onClick={saveCourseSchedule} disabled={scheduleSaving}
                    className="px-3 py-2 bg-blue-800 text-white hover:bg-blue-900 disabled:opacity-50 text-sm flex items-center gap-1 rounded-none"
                    data-testid="course-schedule-save">
                    <FloppyDisk size={14} /> Save override
                  </button>
                  {courseSchedule.source === "course" && (
                    <button onClick={clearCourseSchedule} disabled={scheduleSaving}
                      className="px-3 py-2 border border-slate-300 hover:bg-slate-50 text-sm flex items-center gap-1 rounded-none"
                      data-testid="course-schedule-clear">
                      Clear override
                    </button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Manage per-course custom questions ─────────────────────── */}
        <Card className="border border-amber-200 bg-amber-50/40 rounded-none mb-6" data-testid="manage-feedback-questions-card">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Round 36 · Item 2</p>
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Manage per-course feedback questions
                </h2>
                <p className="text-xs text-slate-600 mt-0.5">
                  Custom questions appear at the top of the student feedback form, before the default rating fields.
                </p>
              </div>
              <div className="flex items-end gap-2">
                <div className="min-w-[240px]">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Course</Label>
                  <Select value={mgmtCourseId} onValueChange={setMgmtCourseId}>
                    <SelectTrigger className="rounded-none w-64 bg-white" data-testid="manage-feedback-course-select">
                      <SelectValue placeholder="Pick a course" />
                    </SelectTrigger>
                    <SelectContent>
                      {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {mgmtLoading ? (
              <p className="text-sm text-slate-500">Loading questions…</p>
            ) : (
              <div className="space-y-2">
                {questions.length === 0 && (
                  <p className="text-sm text-slate-500 italic" data-testid="manage-feedback-empty">
                    No custom questions yet. Click <strong>Add question</strong> to author the first one.
                  </p>
                )}
                {questions.map((q, idx) => (
                  <div
                    key={q.id}
                    className="flex flex-wrap items-end gap-2 p-3 border border-slate-200 bg-white rounded-none"
                    data-testid={`manage-feedback-row-${idx}`}
                  >
                    <div className="flex-1 min-w-[240px]">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Question</Label>
                      <Input
                        value={q.question_text}
                        onChange={e => updateQuestion(idx, { question_text: e.target.value })}
                        placeholder="e.g. How relevant was the case study to your day-to-day school operations?"
                        className="mt-1 rounded-none"
                        data-testid={`manage-feedback-text-${idx}`}
                      />
                    </div>
                    <div className="w-40">
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">Type</Label>
                      <Select value={q.type} onValueChange={v => updateQuestion(idx, { type: v })}>
                        <SelectTrigger className="mt-1 rounded-none" data-testid={`manage-feedback-type-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rating">1–5 Rating</SelectItem>
                          <SelectItem value="text">Free text</SelectItem>
                          <SelectItem value="yes_no">Yes / No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeQuestion(idx)}
                      className="px-3 py-2 border border-red-300 text-red-700 hover:bg-red-50 text-xs flex items-center gap-1 rounded-none"
                      data-testid={`manage-feedback-remove-${idx}`}
                    >
                      <TrashSimple size={14} /> Remove
                    </button>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2 pt-1">
                  <button
                    type="button"
                    onClick={addQuestion}
                    className="px-3 py-2 border border-slate-300 hover:bg-slate-50 text-sm flex items-center gap-1 rounded-none"
                    data-testid="manage-feedback-add"
                  >
                    <Plus size={14} /> Add question
                  </button>
                  <button
                    type="button"
                    onClick={loadDefaultQuestions}
                    className="px-3 py-2 border border-blue-300 text-blue-800 hover:bg-blue-50 text-sm flex items-center gap-1 rounded-none"
                    data-testid="manage-feedback-load-defaults"
                    title="Copy the 6 default questions (course quality, materials, delivery, LMS usability, experience, suggestions) into this course."
                  >
                    <Copy size={14} /> Load default set
                  </button>
                  <button
                    type="button"
                    onClick={saveQuestions}
                    disabled={mgmtSaving || !mgmtCourseId}
                    className="px-3 py-2 bg-blue-800 text-white hover:bg-blue-900 disabled:opacity-50 text-sm flex items-center gap-1 rounded-none"
                    data-testid="manage-feedback-save"
                  >
                    <FloppyDisk size={14} /> {mgmtSaving ? "Saving…" : "Save questions"}
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Submissions inventory ──────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Card className="border border-slate-200 rounded-none"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-widest text-slate-500">Submissions</p><p className="text-2xl font-bold text-slate-950 mt-1" data-testid="feedback-count">{rows.length}</p></CardContent></Card>
          <Card className="border border-slate-200 rounded-none"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-widest text-slate-500">Avg. rating</p><p className="text-2xl font-bold text-slate-950 mt-1 flex items-center gap-1" data-testid="feedback-avg"><Star size={16} weight="fill" className="text-amber-500" /> {avg}</p></CardContent></Card>
        </div>

        <div className="flex flex-wrap gap-2 mb-4 items-end">
          <div className="relative w-full sm:w-72">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search comments / emails…" className="pl-9 rounded-none" data-testid="feedback-search" />
          </div>
          <Select value={programmeFilter} onValueChange={setProgrammeFilter}>
            <SelectTrigger className="rounded-none w-56" data-testid="feedback-programme-filter"><SelectValue placeholder="All programmes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All programmes</SelectItem>
              {programmes.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.title}` : p.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={courseId} onValueChange={setCourseId}>
            <SelectTrigger className="rounded-none w-64" data-testid="feedback-course-filter"><SelectValue placeholder="Course filter" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={exportCsv} className="border border-slate-300 px-3 py-2 text-sm rounded-none hover:bg-slate-50" data-testid="feedback-export-csv">Export CSV</button>
          <button onClick={exportXlsx} className="border border-emerald-300 bg-emerald-50 text-emerald-800 px-3 py-2 text-sm rounded-none hover:bg-emerald-100" data-testid="feedback-export-xlsx">Export Excel</button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200">
            <ChatCircle size={36} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No feedback yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((r, i) => {
              const rating = Number(r.overall_rating || r.rating || 0);
              return (
                <Card key={i} className="border border-slate-200 rounded-none" data-testid={`feedback-row-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{r.user_name || r.user_email || "anonymous"}</p>
                        <p className="text-xs text-slate-500">{r.user_email || ""}</p>
                        <p className="text-xs text-slate-500">
                          {r.course_title || r.course_id}
                          {r.programme_title && <span className="text-slate-400"> · {r.programme_title}</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, idx) => (
                          <Star key={idx} size={12} weight={idx < rating ? "fill" : "regular"} className={idx < rating ? "text-amber-500" : "text-slate-300"} />
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-slate-500 mb-2">
                      {r.content_quality > 0 && <span data-testid={`feedback-content-${i}`}>Content: <b className="text-slate-800">{r.content_quality}/5</b></span>}
                      {r.instructor_rating > 0 && <span data-testid={`feedback-instructor-${i}`}>Instructor: <b className="text-slate-800">{r.instructor_rating}/5</b></span>}
                      {r.pace_rating > 0 && <span data-testid={`feedback-pace-${i}`}>Pace: <b className="text-slate-800">{r.pace_rating}/5</b></span>}
                      {typeof r.would_recommend === "boolean" && <span data-testid={`feedback-recommend-${i}`}>Would recommend: <b className={r.would_recommend ? "text-emerald-700" : "text-red-700"}>{r.would_recommend ? "Yes" : "No"}</b></span>}
                    </div>
                    {r.most_valuable && (
                      <div className="text-sm text-slate-700 mt-2" data-testid={`feedback-most-valuable-${i}`}>
                        <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Most valuable</span>
                        <p className="mt-0.5 whitespace-pre-wrap">{r.most_valuable}</p>
                      </div>
                    )}
                    {r.improvements && (
                      <div className="text-sm text-slate-700 mt-2" data-testid={`feedback-improvements-${i}`}>
                        <span className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Improvements</span>
                        <p className="mt-0.5 whitespace-pre-wrap">{r.improvements}</p>
                      </div>
                    )}
                    {(r.additional_comments || r.comment) && (
                      <div className="text-sm text-slate-700 mt-2" data-testid={`feedback-additional-${i}`}>
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Additional comments</span>
                        <p className="mt-0.5 whitespace-pre-wrap">{r.additional_comments || r.comment}</p>
                      </div>
                    )}
                    <p className="text-[10px] text-slate-400 mt-2">{r.created_at}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
